import {AccessCodeAlphabet} from "@variocube/app-api";
import {
    BoxAccess,
    classNames,
    layout,
    PendingView,
    TabBar,
    TabItem,
    useAsync,
    useBoxes,
    useCountdown,
    useIdle,
    useOccupancies,
    usePlatform,
    usePreventDeactivate,
    View,
    ViewContent
} from "@variocube/cube-ui";
import * as React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {style} from "typestyle";
import {BoxTable} from "./cleaner/BoxTable";
import {DeliveryTable} from "./cleaner/DeliveryTable";
import {PickupTable} from "./cleaner/PickupTable";
import {WelcomePackageTable} from "./cleaner/WelcomePackageTable";
import {CloseableMessage} from "./CloseableMessage";
import {ClesyBoxType, CubeOrder} from "./model";
import {useLocalization} from "../i18n";


class CleanerLog {
    pickupHistory: Map<number, string[]> = new Map();
}

export const cleanerLog = new CleanerLog();

interface CleanerViewProps {
    onDone: () => void;
}

export function CleanerView({onDone}: CleanerViewProps)  {
    const [activeTab, setActiveTab] = useState<CleanerHomeTab>(CleanerHomeTab.Delivery);
    const [store, setStore] = useState<CubeOrder>();
    const [pickup, setPickup] = useState<CubeOrder>();
    const [storeWelcomePackage, setStoreWelcomePackage] = useState<string>();

    usePreventDeactivate();

    const handleStoreDone = useCallback(() => setStore(undefined), []);
    const handlePickupDone = useCallback(() => setPickup(undefined), []);
    const handleStoreWelcomePackageDone = useCallback(() => setStoreWelcomePackage(undefined), []);

    if (store) {
        return <CleanerStore order={store} onDone={handleStoreDone}/>;
    }
    else if (pickup) {
        return <CleanerPickup order={pickup} onDone={handlePickupDone}/>;
    }
    else if (storeWelcomePackage) {
        return <CleanerStoreWelcomePackage boxNumber={storeWelcomePackage} onDone={handleStoreWelcomePackageDone}/>;
    }
    else {
        return (
            <CleanerHome
                onDone={onDone}
                active={activeTab}
                onActiveChanged={setActiveTab}
                onStore={setStore}
                onPickup={setPickup}
                onStoreWelcomePackage={setStoreWelcomePackage}
            />
        );
    }
}


interface CleanerHomeProps {
    active: CleanerHomeTab;
    onActiveChanged: (active: CleanerHomeTab) => void;
    onDone: () => void;
    onStore: (order: CubeOrder) => void;
    onPickup: (order: CubeOrder) => void;
    onStoreWelcomePackage: (boxNumber:string) => void;
}

enum CleanerHomeTab {
    Delivery,
    Pickup,
    WelcomePackages,
    Boxes,
}

function CleanerHome(props: CleanerHomeProps) {
    const {
        active,
        onActiveChanged,
        onDone,
        onStore,
        onPickup,
        onStoreWelcomePackage
    } = props;

    const platform = usePlatform();
    const {t} = useLocalization();

    const idle = useIdle(30);
    const left = useCountdown(60, idle);

    useEffect(() => {
        if (idle && left <= 0) {
            onDone();
        }
    }, [idle, left, onDone]);

    const handleDeliveryClick = useCallback(() => onActiveChanged(CleanerHomeTab.Delivery), [onActiveChanged]);
    const handlePickupClick = useCallback(() => onActiveChanged(CleanerHomeTab.Pickup), [onActiveChanged]);
    const handleWelcomePackagesClick = useCallback(() => onActiveChanged(CleanerHomeTab.WelcomePackages), [onActiveChanged]);
    const handleBoxesClick = useCallback(() => onActiveChanged(CleanerHomeTab.Boxes), [onActiveChanged]);

    const handleOpen = useCallback((boxNumber: string) => platform.openBoxLock(boxNumber), [platform]);

    return (
        <View className={layout.vertical}>
            <div className={classNames(layout.fixed, layout.horizontal)}>
                <TabBar className={layout.fill}>
                    <TabItem active={active == CleanerHomeTab.Delivery} onClick={handleDeliveryClick}>
                        {t("Delivery")}
                    </TabItem>
                    <TabItem active={active == CleanerHomeTab.Pickup} onClick={handlePickupClick}>
                        {t("Pickup")}
                    </TabItem>
                    <TabItem active={active == CleanerHomeTab.WelcomePackages} onClick={handleWelcomePackagesClick}>
                        {t("WelcomePackages")}
                    </TabItem>
                    <TabItem active={active == CleanerHomeTab.Boxes} onClick={handleBoxesClick}>
                        {t("Boxes")}
                    </TabItem>
                </TabBar>
                {idle && (
                    <div className={autoLogoutMessageStyle}>
                        {t("CleanerAutoLogoutMessage", { remainingSeconds: left })}
                    </div>
                )}
                <div className={doneButtonStyle} onClick={onDone}>
                    <div>{t("Logout")}</div>
                </div>
            </div>
            <ViewContent className={layout.paddingMedium}>
                {active == CleanerHomeTab.Delivery && (
                    <DeliveryTable onStore={onStore} onOpen={handleOpen}/>
                )}
                {active == CleanerHomeTab.Pickup && (
                    <PickupTable onPickup={onPickup} />
                )}
                {active == CleanerHomeTab.WelcomePackages && (
                    <WelcomePackageTable onStore={onStoreWelcomePackage} onOpen={handleOpen} />
                )}
                {active == CleanerHomeTab.Boxes && (
                    <BoxTable onOpen={handleOpen}/>
                )}
            </ViewContent>
        </View>
    );
}


const doneButtonStyle = style({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "0.25rem 0.75rem",
    background: "var(--color-primary)",
    color: "#fff",
    textTransform: "uppercase",
    fontWeight: 500,
    fontSize: "1.25em",
});
const autoLogoutMessageStyle = style({
    background: "#f0f0f0",
    flex: "3",
    fontSize: "0.75em",
    textAlign: "right",
    padding: "0.5em"
});


interface CleanerStoreProps {
    order: CubeOrder;
    onDone: () => void;
}

function CleanerStore({order, onDone}: CleanerStoreProps) {
    const platform = usePlatform();
    const {t} = useLocalization();
    const boxes = useBoxes();

    const occupy = useAsync(() => platform.occupy({
        accessCode: order.accessCode,
        type: ClesyBoxType.SuitBag,
        content: {order}
    }), [platform, order]);

    const box = useMemo(() => boxes.find(box => box.number == occupy.result?.boxNumber), [boxes, occupy.result]);

    const handleConfirm = useCallback(async () => {
        await platform.confirmOccupancy(occupy.result!.uuid);
        onDone();
    }, [platform, occupy.result, onDone]);

    const handleCancel = useCallback(async () => {
        await platform.cancelOccupancy(occupy.result!.uuid);
        onDone();
    }, [platform, occupy.result, onDone]);

    if (occupy.error) {
        return <CloseableMessage message={t("OrderStoreError")} onClose={onDone}/>;
    }
    else if (box) {
        return (
            <BoxAccess
                boxes={box}
                platform={platform}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
                allowCancelWhenOpen
                autoConfirm={60}
                boxOpenTitle={t("OrderStoreOpenBoxTitle")}
                boxOpenMessage={t("CleanerStoreOpenBoxMessage", {
                    order: formatOrder(order),
                    boxNames: box.number
                })}
                confirmTitle={t("StoreConfirmTitle")}
                confirmMessage={t("OrderStoreConfirmMessage", {boxNames: box.number})}
                strings={t}
            />
        );
    }
    return <PendingView message={t("OccupancyPending")}/>;
}


interface CleanerStoreWelcomePackageProps {
    boxNumber: string;
    onDone: () => void;
}

function CleanerStoreWelcomePackage({boxNumber, onDone}: CleanerStoreWelcomePackageProps) {
    const platform = usePlatform();
    const {t} = useLocalization();
    const boxes = useBoxes();

    const occupy = useAsync(() => platform.occupy({
        accessCodeShape: {
            alphabet: AccessCodeAlphabet.OneToNine,
            length: 6, // use 6 digits to avoid clash with code generated in backend
        },
        boxNumber,
        content: {
        },
    }), [platform, boxNumber]);

    const box = useMemo(() => boxes.find(box => box.number == occupy.result?.boxNumber), [boxes, occupy.result]);

    const handleCancel = useCallback(async () => {
        if (occupy.result) {
            await platform.cancelOccupancy(occupy.result.uuid);
        }
        onDone();
    }, [platform, occupy.result, onDone]);

    const handleConfirm = useCallback(async () => {
        if (occupy.result) {
            await platform.confirmOccupancy(occupy.result.uuid);
        }
        onDone();
    }, [platform, occupy.result, onDone]);

    if (occupy.error) {
        return <CloseableMessage message={t("OrderStoreError")} onClose={onDone}/>;
    }
    else if (box) {
        return (
            <BoxAccess boxes={box}
                platform={platform}
                onCancel={handleCancel}
                allowCancelWhenOpen
                onConfirm={handleConfirm}
                autoConfirm={60}
                boxOpenTitle={t("WelcomePackageStoreOpenBoxTitle")}
                boxOpenMessage={t("WelcomePackageStoreOpenBoxMessage", {boxNames: box.number})}
                confirmTitle={t("StoreConfirmTitle")}
                confirmMessage={t("WelcomePackageStoreConfirmMessage", {boxNames: box.number})}
                strings={t}
            />
        );
    }
    else {
        return <PendingView message={t("OccupancyPending")}/>;
    }
}

interface CleanerPickupProps {
    order: CubeOrder;
    onDone: () => void;
}

function CleanerPickup({order, onDone}: CleanerPickupProps) {
    const platform = usePlatform();
    const {t} = useLocalization();
    const occupancies = useOccupancies();
    const boxes = useBoxes();

    const {occupancy, box} = useMemo(() => {
        const occupancy = occupancies.find(occupancy => occupancy.accessCode == order.accessCode);
        const box = boxes.find(box => box.number == occupancy?.boxNumber);
        return {occupancy, box};
    }, [occupancies, boxes, order]);

    const handleConfirm = useCallback(async () => {
        if (occupancy) {
            try {
                await platform.endOccupancy(occupancy.uuid);
            }
            catch (error) {
                console.error("Error ending occupancy", error);
            }
            cleanerLog.pickupHistory.set(order.orderId, [occupancy.boxNumber]);
        }
        onDone();
    }, [platform, occupancy])

    if (box) {
        return (
            <BoxAccess
                boxes={box}
                platform={platform}
                onCancel={onDone}
                onConfirm={handleConfirm}
                autoConfirm={60}
                boxOpenTitle={t("OrderPickupOpenBoxTitle")}
                boxOpenMessage={t("CleanerPickupOpenBoxMessage", {
                    order: formatOrder(order),
                    boxNames: box.number
                })}
                confirmTitle={t("OrderPickupConfirmTitle")}
                confirmMessage={t("CleanerPickupConfirmMessage", {boxNames: box.number})}
                strings={t}
            />
        );
    }
    else {
        return <PendingView message={t("PickupPending")}/>;
    }
}


function formatOrder(order: CubeOrder) {
    return `${order.orderIdPrint} ${order.person.lastname} ${order.person.firstname}`;
}

